import type { EmailContext } from "@/types";

/**
 * Detects whether the add-in is in compose or read mode.
 */
export function getItemType(): "compose" | "read" {
  const item = Office.context.mailbox.item;
  if (!item) return "read";

  // In compose mode, subject is a Subject object with get/setAsync
  // In read mode, subject is a plain string
  return typeof item.subject === "string" ? "read" : "compose";
}

/**
 * Read the current email context from Office.js.
 * Works in both compose and read modes.
 */
export async function getEmailContext(): Promise<EmailContext> {
  const item = Office.context.mailbox.item;
  if (!item) {
    return { to: [], cc: [], subject: "", body: "", itemType: "read" };
  }

  const itemType = getItemType();

  if (itemType === "compose") {
    return getComposeContext(item);
  }
  return getReadContext(item);
}

async function getComposeContext(
  item: Office.MessageCompose
): Promise<EmailContext> {
  const [to, cc, subject, body] = await Promise.all([
    asyncToPromise<Office.EmailAddressDetails[]>((cb) =>
      item.to.getAsync(cb)
    ),
    asyncToPromise<Office.EmailAddressDetails[]>((cb) =>
      item.cc.getAsync(cb)
    ),
    asyncToPromise<string>((cb) => item.subject.getAsync(cb)),
    asyncToPromise<string>((cb) =>
      item.body.getAsync(Office.CoercionType.Text, cb)
    ),
  ]);

  return {
    to: to.map((r) => r.emailAddress),
    cc: cc.map((r) => r.emailAddress),
    subject,
    body,
    conversationId: item.conversationId ?? undefined,
    itemType: "compose",
  };
}

function getReadContext(item: Office.MessageRead): Promise<EmailContext> {
  return new Promise((resolve) => {
    item.body.getAsync(Office.CoercionType.Text, (result) => {
      resolve({
        from: item.from?.emailAddress || "",
        to: (item.to || []).map((r) => r.emailAddress),
        cc: (item.cc || []).map((r) => r.emailAddress),
        subject: (item.subject as string) || "",
        body:
          result.status === Office.AsyncResultStatus.Succeeded
            ? result.value
            : "",
        conversationId: item.conversationId ?? undefined,
        itemType: "read",
      });
    });
  });
}

/**
 * Insert HTML content into the email body (compose mode only).
 */
export function insertContent(html: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const item = Office.context.mailbox.item;
    if (!item || getItemType() !== "compose") {
      reject(new Error("Not in compose mode"));
      return;
    }

    (item as Office.MessageCompose).body.setSelectedDataAsync(
      html,
      { coercionType: Office.CoercionType.Html },
      (result) => {
        if (result.status === Office.AsyncResultStatus.Succeeded) {
          resolve();
        } else {
          reject(new Error(result.error?.message || "Failed to insert content"));
        }
      }
    );
  });
}

/**
 * Replace the entire email body (compose mode only).
 */
export function setBody(html: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const item = Office.context.mailbox.item;
    if (!item || getItemType() !== "compose") {
      reject(new Error("Not in compose mode"));
      return;
    }

    (item as Office.MessageCompose).body.setAsync(
      html,
      { coercionType: Office.CoercionType.Html },
      (result) => {
        if (result.status === Office.AsyncResultStatus.Succeeded) {
          resolve();
        } else {
          reject(new Error(result.error?.message || "Failed to set body"));
        }
      }
    );
  });
}

/**
 * Get the sender's email (read mode) or the current user's email.
 */
export function getSenderEmail(): string {
  const item = Office.context.mailbox.item;
  if (!item) return "";

  if (getItemType() === "read") {
    return (item as Office.MessageRead).from?.emailAddress || "";
  }

  return Office.context.mailbox.userProfile?.emailAddress || "";
}

// --- Utility ---

function asyncToPromise<T>(
  fn: (cb: (result: Office.AsyncResult<T>) => void) => void
): Promise<T> {
  return new Promise((resolve, reject) => {
    fn((result) => {
      if (result.status === Office.AsyncResultStatus.Succeeded) {
        resolve(result.value);
      } else {
        reject(new Error(result.error?.message || "Office.js async call failed"));
      }
    });
  });
}
